import React, { useState, useEffect, useCallback } from 'react';
import { WalletIcon, PlusCircleIcon } from '@heroicons/react/24/solid';
import { getBudgetPlanItems, deleteBudgetPlanItem, deleteBudgetPlan } from './viewServices/budget_api';
import { fetchMetadataCategoriesHierarchical } from '../tab_settings/viewServices/services_hierarchicalCategories';
import BudgetGroupedCard from './viewComponents/Manage/CardBudgetGrouped';
import ModalBudgetAdd from './viewComponents/Manage/ModalBudgetAdd';
import Spinner from '../moduleComponents/SpinnerLoad';
import { toast } from 'react-hot-toast';

const normalizePlanItemsList = (data) => {
  const list = data?.results ?? data;
  return Array.isArray(list) ? list : [];
};

const BudgetManage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hierarchicalCategories, setHierarchicalCategories] = useState({});
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const refetchItems = useCallback(async () => {
    try {
      const res = await getBudgetPlanItems();
      setItems(normalizePlanItemsList(res.data));
    } catch {
      toast.error('Failed to refresh budgets.');
    }
  }, []);

  useEffect(() => {
    fetchMetadataCategoriesHierarchical().then(setHierarchicalCategories);
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const res = await getBudgetPlanItems();
        if (!cancelled) setItems(normalizePlanItemsList(res.data));
      } catch {
        if (!cancelled) setError('Failed to fetch budgets.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const handleItemCreated = useCallback((item) => {
    if (item?.id) setItems((prev) => [...prev, item]);
    else refetchItems();
  }, [refetchItems]);

  const handlePlanDuplicated = useCallback(() => {
    refetchItems();
  }, [refetchItems]);

  const handleItemUpdated = useCallback((item) => {
    if (!item?.id) return;
    setItems((prev) => prev.map((x) => (x.id === item.id ? { ...x, ...item } : x)));
  }, []);

  const handleItemDeleted = useCallback((id) => {
    setItems((prev) => prev.filter((x) => x.id !== id));
  }, []);

  // Delete all items of a given L0 within a specific plan
  const handleDeleteGroup = async (planId, l0) => {
    try {
      const ids = items.filter((b) => b.plan === planId && b.class_l0 === l0).map((b) => b.id);
      await Promise.all(ids.map((id) => deleteBudgetPlanItem(id)));
      setItems((prev) => prev.filter((b) => !(b.plan === planId && b.class_l0 === l0)));
      toast.success('Group deleted!');
    } catch {
      toast.error('Failed to delete group.');
    }
  };

  // Delete all items of a given L1 within a specific plan/l0/type
  const handleDeleteL1 = async (planId, l0, l1Id, type) => {
    try {
      const ids = items
        .filter((b) =>
          b.plan === planId &&
          b.class_l0 === l0 &&
          (b.class_l1 || '') === l1Id &&
          b.budget_type === type
        )
        .map((b) => b.id);
      await Promise.all(ids.map((id) => deleteBudgetPlanItem(id)));
      setItems((prev) => prev.filter((b) => !ids.includes(b.id)));
      toast.success('Sub-category deleted!');
    } catch {
      toast.error('Failed to delete sub-category.');
    }
  };

  // Groups items into tree: planId|date|l0 → l1 → l2 → []items
  function groupItems(allItems) {
    const tree = {};
    allItems.forEach((b) => {
      const planId = b.plan;
      const dateKey = (b.plan_date || '').slice(0, 10);
      const l0 = b.class_l0 || '';
      const groupKey = `${planId}|${dateKey}|${l0}`;
      const l1 = b.class_l1 || '';
      const l2 = b.class_l2 || '';
      if (!(groupKey in tree)) tree[groupKey] = {};
      if (!(l1 in tree[groupKey])) tree[groupKey][l1] = {};
      if (!(l2 in tree[groupKey][l1])) tree[groupKey][l1][l2] = [];
      tree[groupKey][l1][l2].push(b);
    });
    return tree;
  }

  function renderGroupedCards(tree) {
    const groupedEntries = Object.entries(tree);
    if (groupedEntries.length === 0) {
      return (
        <div className="flex justify-center">
          <div className="transition-all bg-white rounded-2xl shadow flex flex-col justify-center items-center p-8 min-h-[140px] w-full max-w-md">
            <div className="text-center">
              <div className="text-gray-400 mb-3">
                <WalletIcon className="w-16 h-16 mx-auto" />
              </div>
              <div className="text-gray-500 text-sm font-medium mb-2">No budgets found</div>
            </div>
          </div>
        </div>
      );
    }

    const budgetGroupsByYear = {};
    groupedEntries.forEach(([groupKey, l1s]) => {
      const parts = groupKey.split('|');
      const planId = parseInt(parts[0], 10);
      const date = parts[1] || '';
      const l0 = parts[2] || '';
      const y = date.slice(0, 4);
      const year = /^\d{4}$/.test(y) ? y : '0000';
      if (!budgetGroupsByYear[year]) budgetGroupsByYear[year] = [];
      budgetGroupsByYear[year].push({ groupKey, l1s, planId, l0, date });
    });

    Object.keys(budgetGroupsByYear).forEach((year) => {
      budgetGroupsByYear[year].sort((a, b) => {
        if (a.date !== b.date) return b.date.localeCompare(a.date);
        return (b.l0 || '').localeCompare(a.l0 || '', undefined, { sensitivity: 'base' });
      });
    });

    const years = Object.keys(budgetGroupsByYear).sort((a, b) => Number(b) - Number(a));

    return (
      <div className="space-y-6 px-1">
        {years.map((year) => (
          <div key={year}>
            <div className="flex items-center mb-3 px-2">
              <div className="flex-1 h-px bg-gray-200" />
              <div className="px-3 py-1 bg-gray-100 rounded-full">
                <span className="text-sm font-medium text-gray-600">{year}</span>
              </div>
              <div className="flex-1 h-px bg-gray-200" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 items-start">
              {budgetGroupsByYear[year].map(({ groupKey, l1s, planId, l0, date }) => (
                <BudgetGroupedCard
                  key={groupKey}
                  planId={planId}
                  categL0={l0}
                  groups={l1s}
                  onItemCreated={handleItemCreated}
                  onItemUpdated={handleItemUpdated}
                  onItemDeleted={handleItemDeleted}
                  onDeleteGroup={(pid, l0key) => handleDeleteGroup(pid, l0key)}
                  onDeleteL1={(pid, l0key, l1Id, type) => handleDeleteL1(pid, l0key, l1Id, type)}
                  onPlanDuplicated={handlePlanDuplicated}
                  hierarchicalCategories={hierarchicalCategories}
                  date={date}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="max-w-5xl w-full mx-auto mb-8 mt-2">
      {loading ? (
        <div className="py-12"><Spinner /></div>
      ) : (
        <div>
          {(error || items.length === 0) ? (
            <div className="flex justify-center">
              <div className="transition-all bg-white rounded-2xl shadow flex flex-col justify-center items-center p-8 min-h-[140px] w-full max-w-md">
                <div className="text-center">
                  <div className="text-gray-400 mb-3">
                    <WalletIcon className="w-16 h-16 mx-auto" />
                  </div>
                  <div className="text-gray-500 text-sm font-medium mb-2">
                    {error ? error : 'No budgets found'}
                  </div>
                </div>
              </div>
            </div>
          ) : renderGroupedCards(groupItems(items))}
        </div>
      )}

      <button
        type="button"
        onClick={() => setIsAddModalOpen(true)}
        className="fixed bottom-20 right-6 bg-green-800 hover:bg-green-900 text-white p-3 rounded-full shadow-lg"
        aria-label="Add budget group"
      >
        <PlusCircleIcon className="h-8 w-8" />
      </button>

      <ModalBudgetAdd
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={handleItemCreated}
        hierarchicalCategories={hierarchicalCategories}
      />
    </div>
  );
};

export default BudgetManage;
